import React from 'react';
import { View, StyleSheet, TouchableOpacity, Image, FlatList } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

interface Club {
  id: string;
  name: string;
  category: string;
  coverPhoto: string | null;
  memberCount: number;
  stats: {
    meetupsAccepted: number;
    bIntrosSentAccepted: number;
    bIntrosReceivedAccepted: number;
  };
}

interface ClubsShortcutCardProps {
  clubs: Club[];
}

type NavigationProp = StackNavigationProp<any>;

const ClubsShortcutCard: React.FC<ClubsShortcutCardProps> = ({ clubs }) => {
  const { theme, isDark } = useTheme();
  const navigation = useNavigation<NavigationProp>();

  if (clubs.length === 0) {
    return null;
  }

  const handleClubPress = (clubId: string) => {
    navigation.navigate('Clubs', {
      screen: 'ClubDetail',
      params: { clubId },
    });
  };

  const renderClubItem = ({ item }: { item: Club }) => (
    <TouchableOpacity
      style={[styles.clubItem, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
      onPress={() => handleClubPress(item.id)}
      activeOpacity={0.7}
    >
      <View style={[styles.clubIcon, { backgroundColor: theme.accent }]}>
        {item.coverPhoto ? (
          <Image
            source={{ uri: item.coverPhoto }}
            style={styles.clubImage}
          />
        ) : (
          <Ionicons name="people" size={20} color="#FFFFFF" />
        )}
      </View>
      <View style={styles.clubInfo}>
        <ThemedText style={[styles.clubName, { color: theme.text }]} numberOfLines={1}>
          {item.name}
        </ThemedText>
        <ThemedText style={[styles.clubCategory, { color: theme.textSecondary }]} numberOfLines={1}>
          {item.category}
        </ThemedText>
        {/* Stats Row */}
        <View style={styles.statsRow}>
          <View style={styles.statItem}>
            <Ionicons name="calendar" size={12} color={theme.accent} />
            <ThemedText style={[styles.statText, { color: theme.textTertiary }]}>
              {item.stats.meetupsAccepted}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textTertiary }]}>
              Meetups
            </ThemedText>
          </View>
          <View style={styles.statItem}>
            <Ionicons name="arrow-up" size={12} color="#10B981" />
            <ThemedText style={[styles.statText, { color: theme.textTertiary }]}>
              {item.stats.bIntrosSentAccepted}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textTertiary }]}>
              Sent
            </ThemedText>
          </View>
          <View style={styles.statItem}>
            <Ionicons name="arrow-down" size={12} color="#F59E0B" />
            <ThemedText style={[styles.statText, { color: theme.textTertiary }]}>
              {item.stats.bIntrosReceivedAccepted}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textTertiary }]}>
              Received
            </ThemedText>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
      <View style={styles.header}>
        <View style={[styles.iconContainer, { backgroundColor: theme.accent + '20' }]}>
          <Ionicons name="people" size={24} color={theme.accent} />
        </View>
        <ThemedText style={[styles.title, { color: theme.text }]}>
          My Circles
        </ThemedText>
        <TouchableOpacity
          onPress={() => navigation.navigate('Clubs')}
          style={styles.headerButton}
          activeOpacity={0.7}
        >
          <Ionicons name="arrow-forward" size={20} color={theme.accent} />
        </TouchableOpacity>
      </View>

      <FlatList
        data={clubs.slice(0, 5)}
        renderItem={renderClubItem}
        keyExtractor={(item) => item.id}
        scrollEnabled={false}
        ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
      />

      {clubs.length > 5 && (
        <TouchableOpacity
          style={styles.viewAllButton}
          onPress={() => navigation.navigate('Clubs')}
          activeOpacity={0.7}
        >
          <ThemedText style={[styles.viewAllText, { color: theme.accent }]}>
            View All Clubs
          </ThemedText>
          <Ionicons name="chevron-forward" size={16} color={theme.accent} />
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    flex: 1,
  },
  headerButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  clubItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  clubIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    overflow: 'hidden',
  },
  clubImage: {
    width: '100%',
    height: '100%',
  },
  clubInfo: {
    flex: 1,
  },
  clubName: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  clubCategory: {
    fontSize: 12,
    marginBottom: 6,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  statText: {
    fontSize: 11,
    fontWeight: '600',
  },
  statLabel: {
    fontSize: 9,
    fontWeight: '400',
  },
  clubMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  memberCount: {
    fontSize: 12,
    marginLeft: 4,
  },
  viewAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    paddingVertical: 8,
  },
  viewAllText: {
    fontSize: 14,
    fontWeight: '600',
    marginRight: 4,
  },
});

export default ClubsShortcutCard;
